import { Map, PolygonArea } from 'components/map';
import { getMapDefaultConfig } from 'components/map/atoms';
import { LayerBand, LAYER_ORDER } from 'components/map/ordered-layer';
import { useRef, memo } from 'react';
import { AllocationManagerLineItem } from 'pages/development-areas/show/allocation-manager-types';
import { atomWithStorage } from 'jotai/utils';
import { useAtomValue } from 'jotai';
import style from './allocation-tract-map.module.scss';
import {
  DevelopmentAreaResponse,
  WellFeaturesResponse,
} from 'types/api-responses';
import { Line } from 'components/map/well';
import { LINE_STYLES } from 'components/map/map-styles';
import { buildPolygonStyle, findCenter } from 'lib/map';
import { gray } from '@carbon/colors';
import {
  isCRMTractsVisibleAtom,
  isCRMSubtractsVisibleAtom,
  isTitleAssetsVisibleAtom,
  isWellsVisibleAtom,
  ScopeLayersSelection,
} from 'pages/development-areas/show/scope-layers-selection';

// Module scope, not per-render: a per-render atom mints a fresh identity each
// render, remounting every map layer on each UAT click.
const mapConfigAtom = atomWithStorage(
  'allocation-tract-map-config',
  getMapDefaultConfig()
);

interface AllocationTractMapProps {
  allocationTracts: AllocationManagerLineItem[];
  developmentArea: DevelopmentAreaResponse;
  selectedWellUWI: string;
  selectedTractId?: number | null;
  wellFeatures: WellFeaturesResponse | null;
  selectedAssetGeojson?: any | any[] | null;
  selectedIntersectionGeojson?: any | any[] | null;
  crmTractGeojsons?: any[] | null;
  crmSubtractGeojsons?: any[] | null;
  titleAssetGeojsons?: any[] | null;
}

// The unit reads the same whether or not allocation tracts are drawn over it: a
// faint fill under a solid outline.
//
// It previously switched between gray[20] and gray[70] depending on whether the
// selected row had an allocation manager, so the same boundary changed shade from
// row to row — and the no-tracts case rendered the unit in gray[70], the colour
// tracts use everywhere else. The outline also survives being overpainted, so the
// unit stays findable under a tract that extends past it.
const UNIT_STYLE = buildPolygonStyle({
  color: gray[30],
  lineColor: gray[80],
  fillOpacity: 0.18,
  lineOpacity: 0.9,
  lineWidth: 2,
});

const UnitLayer = memo(
  ({ geometry }: { geometry?: GeoJSON.Polygon | GeoJSON.MultiPolygon }) => {
    if (!geometry) return null;
    return (
      <PolygonArea
        id="development-area"
        geometry={geometry}
        style={UNIT_STYLE}
      />
    );
  }
);

UnitLayer.displayName = 'UnitLayer';

const TractsLayer = memo(
  ({
    tracts,
    selectedId,
    isVisible,
  }: {
    tracts: AllocationManagerLineItem[];
    selectedId?: number | null;
    isVisible: boolean;
  }) => {
    if (!isVisible) return null;

    return (
      <LayerBand band={LAYER_ORDER.allocationTracts}>
        {tracts.map((el, i) => {
          const isSelected = el.unitAllocationTract.id === selectedId;
          const style = isSelected
            ? buildPolygonStyle({
                color: gray[70],
                lineColor: '#0F62FE',
                lineWidth: 4,
                lineOpacity: 1,
              })
            : buildPolygonStyle({ color: gray[70] });
          return (
            <PolygonArea
              geometry={el.unitAllocationTract.geometry}
              id={`uat-tract-${i}`}
              key={`uat-tract-${i}`}
              style={style}
            />
          );
        })}
      </LayerBand>
    );
  }
);

TractsLayer.displayName = 'TractsLayer';

export const AllocationTractMap = ({
  allocationTracts,
  developmentArea,
  selectedWellUWI,
  selectedTractId,
  wellFeatures,
  selectedAssetGeojson,
  selectedIntersectionGeojson,
  crmTractGeojsons = [],
  crmSubtractGeojsons = [],
  titleAssetGeojsons = [],
}: AllocationTractMapProps) => {
  const containerRef = useRef(null);

  const isCRMTractsVisible = useAtomValue(isCRMTractsVisibleAtom);
  const isCRMSubtractsVisible = useAtomValue(isCRMSubtractsVisibleAtom);
  const isTitleAssetsVisible = useAtomValue(isTitleAssetsVisibleAtom);
  const isWellsVisible = useAtomValue(isWellsVisibleAtom);

  const { latitude, longitude } = findCenter(developmentArea.geometry);
  const developmentAreaGeometry = developmentArea.geometry as
    GeoJSON.Polygon | GeoJSON.MultiPolygon;
  const wellGeometry = wellFeatures?.survey || wellFeatures?.stick;

  const scopedLayers = (
    <>
      <section>
        <div className={style.groupChecks}>
          <ScopeLayersSelection />
        </div>
      </section>
    </>
  );

  return (
    <div ref={containerRef} className={style.container}>
      <Map
        className={style.map}
        longitude={longitude}
        latitude={latitude}
        mapConfigAtom={mapConfigAtom}
        showControls
        showMapFilters
        scopedLayers={scopedLayers}
      >
        <UnitLayer geometry={developmentAreaGeometry} />
        {wellGeometry && isWellsVisible && (
          <Line
            id={`${selectedWellUWI}-survey`}
            geojson={wellGeometry}
            style={LINE_STYLES.BLACK_SOLID}
          />
        )}
        {allocationTracts && allocationTracts.length > 0 && (
          <TractsLayer
            tracts={allocationTracts}
            selectedId={selectedTractId}
            isVisible={true}
          />
        )}
        {/* Overlays render below the UAT tracts via z-order band, not a positional
            beforeId — anchoring to the first tract's layer silently dropped the
            overlay whenever that layer wasn't mounted yet. */}
        {isCRMTractsVisible && (
          <LayerBand band={LAYER_ORDER.allocationOverlays}>
            {crmTractGeojsons &&
              crmTractGeojsons.length > 0 &&
              crmTractGeojsons.map((tractGeojson, index) => (
                <PolygonArea
                  key={`crm-tract-${index}`}
                  id={`crm-tract-${index}`}
                  geometry={tractGeojson}
                  color="#39BE83"
                />
              ))}
          </LayerBand>
        )}
        {isCRMSubtractsVisible && (
          <LayerBand band={LAYER_ORDER.allocationOverlays}>
            {crmSubtractGeojsons &&
              crmSubtractGeojsons.length > 0 &&
              crmSubtractGeojsons.map((subtractGeojson, index) => (
                <PolygonArea
                  key={`crm-subtract-${index}`}
                  id={`crm-subtract-${index}`}
                  geometry={subtractGeojson}
                  color="#002D9C"
                />
              ))}
          </LayerBand>
        )}
        {isTitleAssetsVisible && (
          <LayerBand band={LAYER_ORDER.allocationOverlays}>
            {titleAssetGeojsons &&
              titleAssetGeojsons.length > 0 &&
              titleAssetGeojsons.map((titleGeojson, index) => (
                <PolygonArea
                  key={`title-asset-${index}`}
                  id={`title-asset-${index}`}
                  geometry={titleGeojson}
                  color="#A5BED7"
                />
              ))}
          </LayerBand>
        )}
        {selectedIntersectionGeojson &&
          (Array.isArray(selectedIntersectionGeojson) ? (
            selectedIntersectionGeojson.map((geojson, index) => (
              <PolygonArea
                key={`selected-intersection-${index}`}
                id={`selected-intersection-${index}`}
                geometry={geojson}
                color="#ff0000"
              />
            ))
          ) : (
            <PolygonArea
              id="selected-intersection"
              geometry={selectedIntersectionGeojson}
              color="#ff0000"
            />
          ))}
        {selectedAssetGeojson &&
          (Array.isArray(selectedAssetGeojson) ? (
            selectedAssetGeojson.map((geojson, index) => (
              <PolygonArea
                key={`selected-title-tract-${index}`}
                id={`selected-title-tract-${index}`}
                geometry={geojson}
                color="#800080"
              />
            ))
          ) : (
            <PolygonArea
              id="selected-title-tract"
              geometry={selectedAssetGeojson}
              color="#800080"
            />
          ))}
      </Map>
    </div>
  );
};
